import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal data-fetching hook: run a promise, expose {data, loading, error},
 * ignore results from a request that has been superseded.
 */
export const useApi = (fetcher, deps = [], { immediate = true } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const run = useCallback(
    async (...args) => {
      const id = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const result = await fetcher(...args);
        if (id === requestId.current) setData(result);
        return result;
      } catch (err) {
        if (id === requestId.current) setError(err);
        throw err;
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => {
    if (immediate) run().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refresh: run, setData };
};
